import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import Logo from '../../components/common/Logo';
import GoogleSignInButton from '../../components/auth/GoogleSignInButton';
import { EnvelopeIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { resolvePostAuthDestination } from '../../utils/authRedirect';

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});

  const handleLoginChange = (e) => {
    setLoginData({ ...loginData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: '' });
  };

  const validateLogin = () => {
    const newErrors = {};
    if (!loginData.email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(loginData.email)) newErrors.email = 'Email is invalid';
    if (!loginData.password) newErrors.password = 'Password is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (!validateLogin()) return;
    setLoading(true);
    try {
      const response = await login({ email: loginData.email, password: loginData.password });
      navigate(resolvePostAuthDestination(location.state?.from || '/', response?.user?.role), { replace: true });
    } catch (error) {}
    finally { setLoading(false); }
  };

  return (
    <div className="auth-page" style={{ minHeight: '100vh', display: 'flex' }}>

      {/* LEFT — Form */}
      <div
        className="flex-1 flex flex-col px-5 py-4 sm:px-8 sm:py-10"
        style={{ backgroundColor: 'var(--color-background)' }}
      >
        <div>
          <Link
            to="/"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              fontSize: '14px', color: 'var(--gray-10)', textDecoration: 'none',
              fontWeight: 500, fontFamily: "'Poppins', sans-serif",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--orange-9)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--gray-10)')}
          >
            <span style={{ fontSize: '18px', lineHeight: 1 }}>←</span> Back to home
          </Link>
        </div>

        <div className="flex-1 flex items-start justify-center py-6 sm:items-center sm:py-16">
          <div style={{ width: '100%', maxWidth: '460px' }}>

            <div className="mb-6 text-center sm:mb-10">
              <div className="mb-5 flex justify-center sm:mb-10">
                <Logo size="normal" dark className="sm:h-14" />
              </div>
              <h1
                className="mb-2 text-xl font-bold sm:mb-3 sm:text-3xl md:text-[32px]"
                style={{ color: 'var(--gray-12)', fontFamily: "'Poppins', sans-serif" }}
              >
                Sign In
              </h1>
              <p
                className="text-xs sm:text-[15px]"
                style={{
                  color: 'var(--gray-10)',
                  margin: '0 auto',
                  maxWidth: '380px',
                  lineHeight: 1.55,
                  fontFamily: "'Poppins', sans-serif",
                }}
              >
                Sign in to access your dashboard and continue your beauty journey.
              </p>
            </div>

            <form
              onSubmit={handleLoginSubmit}
              className="auth-login-form"
              style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
            >
              <Input
                label="Email"
                type="email"
                name="email"
                value={loginData.email}
                onChange={handleLoginChange}
                icon={<EnvelopeIcon className="w-5 h-5" style={{ color: 'var(--gray-9)' }} />}
                error={errors.email}
                placeholder="Enter your email"
                required
              />

              <div>
                <Input
                  label="Password"
                  type="password"
                  name="password"
                  value={loginData.password}
                  onChange={handleLoginChange}
                  icon={<LockClosedIcon className="w-5 h-5" style={{ color: 'var(--gray-9)' }} />}
                  error={errors.password}
                  showPasswordToggle
                  placeholder="Enter your password"
                  required
                />
                <div style={{ textAlign: 'center', marginTop: '8px' }}>
                  <Link to="/forgot-password" style={{ fontSize: '13px', fontWeight: 500, color: 'var(--orange-9)', textDecoration: 'none' }}>
                    Forgot Password?
                  </Link>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <div style={{ width: '60%' }}>
                  <Button type="submit" fullWidth loading={loading} className="auth-submit-button">
                    Sign In
                  </Button>
                </div>
              </div>

              {/* OR divider */}
              <div className="auth-divider" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
                <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--gray-4)' }} />
                <span style={{ fontSize: '11px', letterSpacing: '0.2em', color: 'var(--gray-9)', textTransform: 'uppercase' }}>Or</span>
                <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--gray-4)' }} />
              </div>

              {/* Social button */}
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <GoogleSignInButton compact />
              </div>

              <p style={{ textAlign: 'center', fontSize: '11px', color: 'var(--gray-9)', marginTop: '12px', lineHeight: 1.6, fontFamily: "'Poppins', sans-serif" }}>
                By continuing, you agree to The Salon's{' '}
                <Link to="/terms" style={{ color: 'var(--orange-9)', textDecoration: 'underline' }}>Terms and Conditions</Link>,{' '}
                <Link to="/privacy" style={{ color: 'var(--orange-9)', textDecoration: 'underline' }}>Privacy Policy</Link>
                {' '}&amp;{' '}
                <Link to="/policies" style={{ color: 'var(--orange-9)', textDecoration: 'underline' }}>Booking Policy</Link>
              </p>
            </form>

            <div className="auth-signup-link" style={{ textAlign: 'center', marginTop: '24px' }}>
              <p style={{ fontSize: '14px', color: 'var(--gray-10)', margin: 0 }}>
                Don't have an Account?{' '}
                <Link to="/register" style={{ fontWeight: 600, color: 'var(--orange-9)', textDecoration: 'none' }}>Sign Up</Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT — Organic blob art panel (matches About section) */}
      <div
        className="hidden lg:flex flex-col justify-center"
        style={{
          width: '45%',
          padding: '60px 56px',
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: '#faf9fb',
        }}
      >
        {/* Art block */}
        <div style={{ position: 'relative', width: '100%', height: '420px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '40px' }}>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              margin: 'auto',
              width: '92%',
              height: '88%',
              backgroundColor: 'rgba(240, 90, 53, 0.12)',
              borderRadius: '62% 38% 54% 46% / 48% 56% 44% 52%',
            }}
          />
          <div
            className="auth-float"
            style={{
              position: 'relative',
              zIndex: 10,
              width: '58%',
              aspectRatio: '1 / 1',
              borderRadius: '50%',
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
              border: '4px solid #fff',
            }}
          >
            <img
              src="https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?auto=format&fit=crop&q=80&w=900"
              alt="Stylist at work"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
          <div
            className="auth-float"
            style={{
              position: 'absolute',
              top: '24px',
              right: '32px',
              zIndex: 20,
              width: '80px',
              height: '80px',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2)',
              border: '4px solid #fff',
            }}
          >
            <img
              src="https://images.unsplash.com/photo-1560066984-138dadb4c035?auto=format&fit=crop&q=80&w=400"
              alt="Hair color"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
          <div
            className="auth-float"
            style={{
              position: 'absolute',
              bottom: '24px',
              left: '24px',
              zIndex: 20,
              width: '96px',
              height: '96px',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2)',
              border: '4px solid #fff',
            }}
          >
            <img
              src="https://images.unsplash.com/photo-1580618672591-eb180b1a973f?auto=format&fit=crop&q=80&w=400"
              alt="Finished look"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
        </div>

        {/* Copy */}
        <div style={{ maxWidth: '440px', textAlign: 'left' }}>
          <h2 style={{ fontSize: '28px', fontWeight: 700, color: 'var(--gray-12)', margin: '0 0 16px', lineHeight: 1.2, fontFamily: "'Poppins', sans-serif" }}>
            Welcome back to your salon dashboard
          </h2>
          <p style={{ fontSize: '15px', color: 'var(--gray-11)', lineHeight: 1.7, margin: 0, fontFamily: "'Poppins', sans-serif" }}>
            Every cut, every color, every consultation — designed around who you are and how you want
            to be seen. Sign in to manage your appointments, revisit your favorite stylists, and pick
            up right where you left off.
          </p>
        </div>
      </div>

      {/* Float keyframes */}
      <style>{`
        @keyframes authFloatA { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-14px); } }
        @keyframes authFloatB { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(14px); } }
        .auth-float:nth-of-type(1) { animation: authFloatA 4s ease-in-out infinite; }
        .auth-float:nth-of-type(2) { animation: authFloatB 4.6s ease-in-out infinite; }
        .auth-float:nth-of-type(3) { animation: authFloatA 5.2s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

export default AuthPage;
